/*
* Resideo/LifeWhere
* Copyright (C) 2018-2023 Resideo/LifeWhere
* mailto:nathan.williams@resideo.com
*/

import React, { Component } from 'react';
import { Spinner } from 'react-bootstrap';
import AlertService from '../../services/alerts.service';
import AssetService from '../../services/asset.service';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ReferenceArea, ReferenceDot, ReferenceLine, ResponsiveContainer, CartesianGrid } from 'recharts';
import { DefaultTooltipContent } from 'recharts/lib/component/DefaultTooltipContent';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { RunRightPanel } from '../Graph/RunRightPanel';
import { ACRightPanel } from '../Graph/ACRightPanel';
import HighchartGraph from '../Graph/HighchartGraph';
import { Event } from '../GoogleAnalytics';
import {AxisTypes, Equipment, GraphTypes, jsSource, thermostatDataVariables} from "../../componentObjects";
import 'react-tabs/style/react-tabs.css';
import "../../CSS/DataModal.css";
import moment from 'moment';
import GraphComponent from "../Graph/GraphComponent";
import IconButton from "@mui/material/IconButton";
import Loader from "../Loader/Loader";
import FullLoader from "../Loader/FullLoader";

class AlertGraphsModal extends Component {

    constructor(props) {
        super(props);

        this.handleCheckbox = this.handleCheckbox.bind(this);
        this.onChangeRun = this.onChangeRun.bind(this);
        this.onRightRunClick = this.onRightRunClick.bind(this);
        this.onLeftRunClick = this.onLeftRunClick.bind(this);
        this.runAnalytic = this.runAnalytic.bind(this);
        this.onSetpointClick = this.onSetpointClick.bind(this);
        this.getHighchartsData = this.getHighchartsData.bind(this);
        this.onExtendedLeftClick = this.onExtendedLeftClick.bind(this);
        this.onExtendedRightClick = this.onExtendedRightClick.bind(this);
        this.getAlertStartEndLines = this.getAlertStartEndLines.bind(this);

        this.state = {
            acSelected: false,
            alertSelected: false,
            canLeftClick: false,
            canRightClick: false,
            currentRunName: "",
            currentSource: jsSource,
            selectedRun: 0,
            showRunAnnotations: false,
            showZoomButtom: false,
            visibility: {
                analytics: false,
                annotations: false,
                furnace: true,
                temp: true,
                rla: true,
                lra: false,
                blowerCurrent: true,
                inducerCurrent: true,
                coolSetpoint: false,
                heatSetpoint: false,
                stageAvg: true,
                thermostat: true,
                weather: true
            },
        }
    }

    onClick = e => {
        this.props.onClick && this.props.onClick(e);
    };

    onCancelClick() {
        this.props.clearGraphData();
    };

    showZoomButton(show) {
        this.setState({ showZoomButton: show });
    }

    runAnalytic() {
        this.setState({ showRunAnnotations: !this.state.showRunAnnotations }, () => {
            this.setState(prevState => ({
                visibility: {
                    ...prevState.visibility,
                    analytics: this.state.showRunAnnotations,
                    annotations: this.state.showRunAnnotations,
                }
            }));
            if (this.state.showRunAnnotations)
                Event("Run Analytics Clicked", "User clicked run analytics on alert graph", "Run Analytics Clicked");
        });
    }

    onSetpointClick(spType) {
        if (spType == "cool")
            this.setState(prevState => ({
                visibility: {
                    ...prevState.visibility,
                    coolSetpoint: !this.state.visibility.coolSetpoint
                }
            }));
        else if (spType == "heat")
            this.setState(prevState => ({
                visibility: {
                    ...prevState.visibility,
                    heatSetpoint: !this.state.visibility.heatSetpoint
                }
            }));
    }

    handleCheckbox(e) {
        const checkedVal = e.target.value;

        if (e.target.checked) {
            this.setState(prevState => ({
                visibility: {
                    ...prevState.visibility,
                    [checkedVal]: true
                }
            }));
        }
        else {
            this.setState(prevState => ({
                visibility: {
                    ...prevState.visibility,
                    [checkedVal]: false
                }
            }));
        }
    }

    async onRightRunClick() {
        let numRuns = this.props.currentGraph.timeSeriesDataSources.length;
        let index = this.state.selectedRun + 1;
        let canLeft = index > 0 ? true : false;
        let canRight = index < numRuns - 1 ? true : false;

        this.setState({
            currentRunName: this.props.currentGraph.runNamesList[index],
            currentSource: this.props.currentGraph.timeSeriesDataSources[index],
            clearPreviusState: true,
            selectedRun: index,
            canLeftClick: canLeft,
            canRightClick: canRight
        }, () => {
            var runChar = this.state.currentRunName.charAt(0);
            if (runChar == 'g' || runChar == 'r')
                this.setState({ untrainedRun: false });
            else
                this.setState({ untrainedRun: true });
        });

    }

    async onLeftRunClick() {
        let numRuns = this.props.currentGraph.timeSeriesDataSources.length;
        let index = this.state.selectedRun - 1;
        let canLeft = index > 0 ? true : false;
        let canRight = index < numRuns - 1 ? true : false;

        this.setState({
            currentRunName: this.props.currentGraph.runNamesList[index],
            currentSource: this.props.currentGraph.timeSeriesDataSources[index],
            clearPreviusState: true,
            selectedRun: index,
            canLeftClick: canLeft,
            canRightClick: canRight
        }, () => {
            var runChar = this.state.currentRunName.charAt(0);
            if (runChar == 'g' || runChar == 'r')
                this.setState({ untrainedRun: false });
            else
                this.setState({ untrainedRun: true });
        });
    }

    async onChangeRun(e) {
        let numRuns = this.props.currentGraph.timeSeriesDataSources.length;
        let index = parseInt(e.target.value, 10);
        let canLeft = index > 0 ? true : false;
        let canRight = index < numRuns - 1 ? true : false;

        this.setState({
            currentRunName: this.props.currentGraph.runNamesList[index],
            currentSource: this.props.currentGraph.timeSeriesDataSources[index],
            clearPreviusState: true,
            selectedRun: index,
            canLeftClick: canLeft,
            canRightClick: canRight
        }, () => {
            var runChar = this.state.currentRunName.charAt(0);
            if (runChar == 'g' || runChar == 'r')
                this.setState({ untrainedRun: false });
            else
                this.setState({ untrainedRun: true });
        });
    }
    
    async onExtendedLeftClick() {
        this.props.onExtendLeftClick();
    }
    
    async onExtendedRightClick() {
        this.props.onExtendRightClick();
    }

    getAlertStartEndLines() {
        let plotLines = [];
        let lineTime = moment(this.props.currentAlert.startTime).add(moment().utcOffset()*-1, 'minutes').valueOf();
        let line = {
            color: '#dc3545',
            value: lineTime,
            width: 2,
            label: {
                text: "Alert Start",
                verticalAlign: 'top',
                textAlign: 'left',
                rotation: 0,
                y: 15
            }
        };
        plotLines = plotLines.concat(line);

        lineTime = moment(this.props.currentAlert.endTime).add(moment().utcOffset()*-1, 'minutes').valueOf();
        let line2 = {
            color: '#dc3545',
            value: lineTime,
            width: 2,
            label: {
                text: "Alert End",
                verticalAlign: 'top',
                textAlign: 'left',
                rotation: 0,
                y: 15
            }
        };
        plotLines = plotLines.concat(line2);
        return plotLines;
    }

    getHighchartsAnnotations(graphAnnotations) {
        let plotBands = [];

        if (graphAnnotations !== null && graphAnnotations !== undefined && graphAnnotations.length) {
            for (let i = 0; i < graphAnnotations.length; i++) {
                let annotation = graphAnnotations[i];
                let bandStart = moment(annotation["startTime"]).valueOf();
                let bandEnd = moment(annotation["endTime"]).valueOf();

                let band = {
                    color: annotation["color"] + "45", /* opacity */
                    from: bandStart,
                    to: bandEnd,
                    label: {
                        text: annotation["state"],
                        rotation: -90,
                        textAlign: 'left',
                        verticalAlign: 'top',
                        y: 90
                    },
                    zIndex: 1
                };

                plotBands = plotBands.concat(band);
            }
        }

        return plotBands;
    }

    getHighchartsData(currentHCSource) {
        let chartDataList = [];
        let fullChartDataList = currentHCSource;
        if (fullChartDataList !== null && fullChartDataList !== undefined) {
            let acCurrent = fullChartDataList?.find(x => x.name === "AC Current" || x.name === "Air Handler Current");
            if (acCurrent !== null && acCurrent !== undefined) {
                chartDataList = chartDataList.concat(acCurrent);
            }

            if (this.state.visibility.temp) {
                let indoorHubTemp = fullChartDataList?.find(x => x.name === "Indoor Hub Temp");
                if (indoorHubTemp !== null && indoorHubTemp !== undefined) {
                    chartDataList = chartDataList.concat(indoorHubTemp);
                }
                let outdoorHubTemp = fullChartDataList?.find(x => x.name === "Outdoor Hub Temp");
                if (outdoorHubTemp !== null && outdoorHubTemp !== undefined) {
                    chartDataList = chartDataList.concat(outdoorHubTemp);
                }
            }
            if (this.state.visibility.furnace) {
                let furnaceCurrent = fullChartDataList?.find(x => x.name === "Furnace Current");
                if (furnaceCurrent !== null && furnaceCurrent !== undefined) {
                    chartDataList = chartDataList.concat(furnaceCurrent);
                }
            }
            if (this.state.visibility.weather) { 
                let temp = fullChartDataList?.find(x => x.name === "Temperature");
                if (temp !== null && temp !== undefined) {
                    chartDataList = chartDataList.concat(temp);
                }
                let humidity = fullChartDataList?.find(x => x.name === "Humidity");
                if (humidity !== null && humidity !== undefined) {
                    chartDataList = chartDataList.concat(humidity);
                }
            }
            if (this.props.isSensorAlert && this.state.visibility.blowerCurrent) {
                let blowerCurrent = fullChartDataList?.find(x => x.name === "Blower Current");
                if (blowerCurrent !== null && blowerCurrent !== undefined) {
                    chartDataList = chartDataList.concat(blowerCurrent);
                }
            }
            if (this.props.isSensorAlert && this.state.visibility.inducerCurrent) {
                let inducerCurrent = fullChartDataList?.find(x => x.name === "Inducer Current");
                if (inducerCurrent !== null && inducerCurrent !== undefined) {
                    chartDataList = chartDataList.concat(inducerCurrent);
                }
            }
            if (this.state.visibility.thermostat) {
                let thermoIndoorTemp = fullChartDataList?.find(x => x.name === thermostatDataVariables.TstatIndoorTemp.name);
                if (thermoIndoorTemp !== null && thermoIndoorTemp !== undefined) {
                    chartDataList = chartDataList.concat(thermoIndoorTemp);
                }
                let thermoOutdoorTemp = fullChartDataList?.find(x => x.name === thermostatDataVariables.TstatOutdoorTemp.name);
                if (thermoOutdoorTemp !== null && thermoOutdoorTemp !== undefined) {
                    chartDataList = chartDataList.concat(thermoOutdoorTemp);
                }
                let thermoIndoorHum = fullChartDataList?.find(x => x.name === thermostatDataVariables.IndoorHum.name);
                if (thermoIndoorHum !== null && thermoIndoorHum !== undefined) {
                    chartDataList = chartDataList.concat(thermoIndoorHum);
                }
                let thermoOutdoorHum = fullChartDataList?.find(x => x.name === thermostatDataVariables.OutdoorHum.name);
                if (thermoOutdoorHum !== null && thermoOutdoorHum !== undefined) {
                    chartDataList = chartDataList.concat(thermoOutdoorHum);
                }
                let thermoCoolSp = fullChartDataList?.find(x => x.name === thermostatDataVariables.CoolSetpoint.name);
                if (thermoCoolSp !== null && thermoCoolSp !== undefined) {
                    chartDataList = chartDataList.concat(thermoCoolSp);
                }
                let thermoHeatSp = fullChartDataList?.find(x => x.name === thermostatDataVariables.HeatSetpoint.name);
                if (thermoHeatSp !== null && thermoHeatSp !== undefined) {
                    chartDataList = chartDataList.concat(thermoHeatSp);
                }
            }
            if (this.state.visibility.rla) {
                let rlaLine = fullChartDataList?.find(x => x.name === 'RLA');
                if (rlaLine !== null && rlaLine !== undefined) {
                    chartDataList = chartDataList.concat(rlaLine);
                }
            }
            if (this.state.visibility.lra) {
                let lraLine = fullChartDataList?.find(x => x.name === 'LRA');
                if (lraLine !== null && lraLine !== undefined) {
                    chartDataList = chartDataList.concat(lraLine);
                }
            }
            
            if (this.state.visibility.analytics || this.state.showRunAnnotations) {
                let blowerAnn = fullChartDataList?.find(x => x.name === 'Blower');
                if (blowerAnn !== null && blowerAnn !== undefined) {
                    chartDataList = chartDataList.concat(blowerAnn)
                }
                let compressorAnn = fullChartDataList?.find(x => x.name === 'Compressor');
                if (compressorAnn !== null && compressorAnn !== undefined) {
                    chartDataList = chartDataList.concat(compressorAnn)
                }
            }
            
            if (this.props.isAirHandler) {
                if (this.state.visibility.stageAvg) {
                    let expectedAnns = fullChartDataList?.filter(x => x.name.includes("Expected"));
                    if (expectedAnns !== null && expectedAnns !== undefined && expectedAnns.length) {
                        chartDataList.push(...expectedAnns);
                    }

                    let avgAnns = fullChartDataList?.filter(x => x.name.includes("Average"));
                    if (avgAnns !== null && avgAnns !== undefined && avgAnns.length) {
                        chartDataList.push(...avgAnns);
                    }
                }
            }
        }
        return chartDataList;
    }

    renderACGraph(isRunPanel, isExtendedPanel = false) {
        let hasAnalytics = this.props.currentAsset.hasAnalytics;
        let currentSource = this.props.currentGraph !== undefined ? this.props.currentGraph.highchartsRun : null; 
        let canRightClick = (this.state.selectedRun === 0 && this.props.currentGraph !== undefined && this.props.currentGraph.timeSeriesDataSources !== null && this.props.currentGraph.timeSeriesDataSources !== undefined && this.props.currentGraph.timeSeriesDataSources.length) ? true : this.state.canRightClick;
        
        let acRunPanel = currentSource !== null && currentSource !== undefined && currentSource.data !== undefined && currentSource.data.length ?
            <div className="margin-top-10 row">
                <div className="col-sm-12 col-md-8">
                    <div className="highlight-bar-charts" >
                        <GraphComponent graphType={GraphTypes.ASSET} chartData={this.getHighchartsData(this.props.runDataList)} key={`${this.props.currentAsset.assetId}-${currentSource.timeString}-${this.props.runDataList.length}-${this.state.visibility}`} assetType={Equipment.AIR_CONDITIONER}
                            visibility={this.state.visibility} rlaValue={this.props.currentGraph.rla} lraValue={this.props.currentGraph.lra} flaValue={null} graphAnnotations={this.getHighchartsAnnotations(this.state.currentSource.graphAnnotations)} showAnnotations={this.state.showRunAnnotations}
                            hasAnalytics={this.props.currentAsset.hasAnalytics} title={currentSource.name} 
                            pointStart={currentSource.data[0]} secondAxisType={AxisTypes.TEMP} currentAlert={this.props.currentAlert}/>

                        <br /><br />
                    </div>
                </div>
                <div className="col-sm-12 col-md-4">
                    <ACRightPanel isFullScreen={true} isDaily={false} showAnnotations={this.state.showRunAnnotations} onClick1={this.runAnalytic} onClick2={() => { this.child.zoomOut(); }} visibility={this.state.visibility}
                        handleCheckbox={this.handleCheckbox} zoomOutEnabled={this.state.showZoomButton} hasAnalytics={this.props.currentAsset.hasAnalytics == true ? true : false} canLeftClick={this.state.canLeftClick == true ? true : false} canRightClick={canRightClick == true ? true : false}
                        runNamesList={this.props.currentGraph.runNamesList} selectedRun={this.state.selectedRun} onChangeRun={this.onChangeRun} onLeftRunClick={this.onLeftRunClick} onRightRunClick={this.onRightRunClick} />
                </div>
            </div>
            : <div className="centered"><br />No Data found!</div>;

        let acDailyPanel = this.props.currentGraph !== undefined && this.props.currentGraph.highchartsDaily !== null && this.props.currentGraph.highchartsDaily !== undefined && this.props.currentGraph.highchartsDaily.data.length && this.props.timeLineDataList.length ?
            <div className="margin-top-10 row">
                <div className="col-sm-12 col-md-8">
                    <div className="highlight-bar-charts" >
                        <GraphComponent graphType={GraphTypes.ASSET} chartData={this.getHighchartsData(this.props.timeLineDataList)} key={`${this.props.currentGraph.highchartsDaily.name}-${this.state.visibility}-timeline`} isAirConditioner={true} 
                            visibility={this.state.visibility} rlaValue={this.props.currentGraph.rla} lraValue={this.props.currentGraph.lra} flaValue={null} graphAnnotations={this.getHighchartsAnnotations(this.props.currentGraph.daily.graphAnnotations)}
                            hasAnalytics={hasAnalytics} title={currentSource?.name} 
                            pointStart={this.props.currentGraph.highchartsDaily.data[0]} secondAxisType={AxisTypes.TEMP} currentAlert={this.props.currentAlert}/>

                        <br /><br />
                    </div>
                </div>
                <div className="col-sm-12 col-md-4">
                    <ACRightPanel isFullScreen={true} isDaily={true} showAnnotations={this.state.showRunAnnotations} onClick1={this.runAnalytic} onClick2={() => { this.child.zoomOut(); }}
                        handleCheckbox={this.handleCheckbox} visibility={this.state.visibility} zoomOutEnabled={this.state.showZoomButton} hasAnalytics={hasAnalytics} />
                </div>
            </div>
            : <div className="centered"><br />No Data found!</div>;
        
        let extendedLeftButton = this.props.isLongAlert ?
            <div className={"extended-icon extended-left col-sm-1 col-md-1"}>
                <IconButton onClick={this.onExtendedLeftClick}>
                    <i className={"fa-solid fa-arrow-left extended-color"}></i>
                </IconButton>
            </div>
            : null;
        let extendedRightButton = this.props.isLongAlert ?
            <div className={"extended-icon extended-right col-sm-1 col-md-1"}>
                <IconButton onClick={this.onExtendedRightClick} disabled={this.props.extendRightDisabled}>
                    <i className={this.props.extendRightDisabled ? "fa-solid fa-arrow-right" : "fa-solid fa-arrow-right extended-color"}></i>
                </IconButton>
            </div>
            : null;

        let acExtendedPanel = this.props.currentGraph !== undefined && this.props.currentGraph.highchartsExtended !== null && this.props.currentGraph.highchartsExtended !== undefined && this.props.currentGraph.highchartsExtended.data.length && this.props.extendedDataList.length ?
            <div className="margin-top-10 row">
                {extendedLeftButton}
                <div className="col-sm-10 col-md-8">
                    <div className="highlight-bar-charts" >
                        <GraphComponent graphType={GraphTypes.ASSET} chartData={this.getHighchartsData(this.props.extendedDataList)} key={`${this.props.currentGraph.highchartsExtended.name}-${this.state.visibility}-timeline`} assetType={Equipment.AIR_CONDITIONER}
                                        visibility={this.state.visibility} rlaValue={this.props.currentGraph.rla} lraValue={this.props.currentGraph.lra} flaValue={null} graphAnnotations={this.getHighchartsAnnotations(this.props.currentGraph.extended.graphAnnotations)}
                                        hasAnalytics={hasAnalytics} title={currentSource.name} alertAnnotations={this.getAlertStartEndLines()} isLongAlert={this.props.isLongAlert}
                                        pointStart={this.props.currentGraph.highchartsExtended.data[0]} secondAxisType={AxisTypes.TEMP} currentAlert={this.props.currentAlert}/>

                        <br /><br />
                    </div>
                </div>
                {extendedRightButton}
                <div className="col-sm-12 col-md-2">
                    <ACRightPanel isFullScreen={true} isDaily={true} showAnnotations={this.state.showRunAnnotations} onClick1={this.runAnalytic} onClick2={() => { this.child.zoomOut(); }}
                                  handleCheckbox={this.handleCheckbox} visibility={this.state.visibility} zoomOutEnabled={this.state.showZoomButton} hasAnalytics={hasAnalytics} />
                </div>
            </div>
            : <div className="centered"><br />No Data found!</div>;

        let acPanel = isRunPanel ? acRunPanel : (isExtendedPanel ? acExtendedPanel : acDailyPanel);

        return (acPanel);
    }

    renderBody() {
        let hasAnalytics = this.props.currentAsset.hasAnalytics;
        let currentSource = this.props.currentGraph; //this.state.currentSource ? this.state.currentSource : this.props.currentGraph.timeSeriesDataSources[0];
        let acGraph = this.props.currentAlert.assetClassNames.some(a => a === "AirConditioner") ? true : false;
        
        let multiGraph = this.props.multiseriesGraph !== null && this.props.multiseriesGraph !== undefined && this.props.multiseriesGraph.series !== null && this.props.multiseriesGraph.series !== undefined && this.props.multiseriesGraph.series[0] !== undefined && this.props.multiseriesGraph.series[0] !== null && this.props.multiseriesGraph.series[0].data[0] !== undefined && this.props.multiDataList.length ?
            <div>
                <GraphComponent graphType={GraphTypes.MULTISERIES} chartData={this.props.multiDataList} key={`Multiseries-${this.props.multiseriesGraph.series.length}`} graph={this.props.multiseriesGraph} assetType={Equipment.AIR_CONDITIONER} dataMax={this.props.multiseriesGraph.yDataMax}
                    visibility={this.state.visibility} rlaValue={this.props.currentGraph.rla} lraValue={this.props.currentGraph.lra} flaValue={null} graphAnnotations={this.getHighchartsAnnotations(this.props.multiseriesGraph.graphAnnotations)}
                    hasAnalytics={hasAnalytics} title={null} 
                    pointStart={this.props.multiseriesGraph.series[0].data[0].timestamp} secondAxisType={AxisTypes.TEMP}
                                currentAlert={this.props.currentAlert}  />
            </div>
            : <div className="centered"><br />No Data found!</div>;

        let runPanel = acGraph ?
            <div>{this.renderACGraph(true)}</div>
            : (this.props.currentGraph.run !== null && this.props.currentGraph.run !== undefined && this.props.currentGraph.run.series !== null && this.props.currentGraph.run.series !== undefined && this.props.currentGraph.run.series[0] !== null && this.props.currentGraph.run.series[0] !== undefined && this.props.currentGraph.run.series[0].data[0] !== undefined && this.props.runDataList.length ?
                <div className="margin-top-10 row">
                    <div className="col-sm-12 col-md-10">
                        <div className="highlight-bar-charts" >
                            <GraphComponent graphType={GraphTypes.ASSET} chartData={this.getHighchartsData(this.props.runDataList)} key={`${currentSource !== undefined ? currentSource.timeString : null}-${this.props.runDataList.length}-${this.state.visibility}`} assetType={Equipment.FURNACE} dataMax={this.props.currentGraph.run.yDataMax}
                                visibility={this.state.visibility} rlaValue={this.props.currentGraph.rla} lraValue={this.props.currentGraph.lra} flaValue={null} graphAnnotations={this.getHighchartsAnnotations(this.props.currentGraph.run.graphAnnotations)} 
                                hasAnalytics={hasAnalytics}
                                title={currentSource !== undefined ? currentSource.timeString : ""} pointStart={this.props.currentGraph.run.series[0].data[0].timestamp} 
                                isMultiCT={this.props.currentAsset.isMultiCT} secondAxisType={AxisTypes.TEMP} currentAlert={this.props.currentAlert}/>
                                
                            <br /><br />
                        </div>
                    </div>
                    <div className="margin-top-20 col-sm-4 col-md-2">
                        <RunRightPanel isFullScreen={true} showAnnotations={this.state.showRunAnnotations} showStageAvg={this.state.visibility.stageAvg}
                            onClick1={this.runAnalytic.bind(this)} onClick2={() => { this.child.zoomOut(); }} handleCheckbox={this.handleCheckbox}
                            zoomOutEnabled={this.state.showZoomButton} hasAnalytics={hasAnalytics} isAirHandler={this.props.isAirHandler} />
                    </div>
                </div>
                : <div className="centered"><br />No Data found!</div>);

        let dailyPanel = acGraph ?
            <div>{this.renderACGraph(false)}</div>
            : (this.props.currentGraph.daily !== null && this.props.currentGraph.daily !== undefined && this.props.currentGraph.daily.series !== null &&
                this.props.currentGraph.daily.series !== undefined && this.props.currentGraph.daily.series[0] !== null && this.props.currentGraph.daily.series[0] !== undefined &&
                this.props.currentGraph.daily.series[0].data[0] !== undefined && this.props.timeLineDataList.length ?

                (this.props.isThermostat ?
                    <div className="margin-top-10 row">
                        <div className="col-sm-12 col-md-10">
                            <GraphComponent graphType={GraphTypes.ASSET} chartData={this.getHighchartsData(this.props.timeLineDataList)} key={`${this.props.currentGraph.daily.timeString}-${this.props.timeLineDataList.length}-${this.state.visibility}-timeline`} assetType={Equipment.THERMOSTAT} dataMax={this.props.currentGraph.daily.yDataMax} yAxisLabel={this.props.currentGraph.daily.yDataLabel}
                                visibility={this.state.visibility} rlaValue={this.props.currentGraph.rla} lraValue={this.props.currentGraph.run.lra} flaValue={null} graphAnnotations={this.getHighchartsAnnotations(this.props.currentGraph.daily.graphAnnotations)} 
                                hasAnalytics={hasAnalytics} title={this.props.currentGraph.daily.timeString} pointStart={this.props.currentGraph.daily.series[0].data[0].timestamp} 
                                isMultiCT={this.props.currentAsset.isMultiCT} secondAxisType={AxisTypes.NONE} currentAlert={this.props.currentAlert} />

                            <br /><br />
                        </div>
                        <div className="margin-top-20 col-sm-4 col-md-2">
                            <RunRightPanel isFullScreen={true} showAnnotations={this.state.showRunAnnotations} showStageAvg={this.state.visibility.stageAvg}
                                onClick1={this.runAnalytic.bind(this)} onClick2={() => { this.child.zoomOut(); }}
                                onClick3={() => this.onSetpointClick} isThermostat={this.props.isThermostat} isAirHandler={this.props.isAirHandler}
                                    zoomOutEnabled={this.state.showZoomButton} hasAnalytics={hasAnalytics} handleCheckbox={this.handleCheckbox} />
                            <br /><br />
                        </div>
                    </div>
                    : <div className="margin-top-10 row">
                        <div className="col-sm-12 col-md-10">
                            <GraphComponent graphType={GraphTypes.ASSET} chartData={this.getHighchartsData(this.props.timeLineDataList)} key={`${this.props.currentGraph.daily.timeString}-${this.props.timeLineDataList.length}-${this.state.visibility}-timeline`} assetType={Equipment.FURNACE} dataMax={this.props.currentGraph.daily.yDataMax}
                                visibility={this.state.visibility} rlaValue={this.props.currentGraph.rla} lraValue={this.props.currentGraph.run.lra} flaValue={null} graphAnnotations={this.getHighchartsAnnotations(this.props.currentGraph.daily.graphAnnotations)} 
                                hasAnalytics={hasAnalytics} title={this.props.currentGraph.daily.timeString} 
                                pointStart={this.props.currentGraph.daily.series[0].data[0].timestamp} secondAxisType={AxisTypes.TEMP} currentAlert={this.props.currentAlert}/>

                            <br /><br />
                        </div>
                        <div className="margin-top-20 col-sm-4 col-md-2">
                            <RunRightPanel isFullScreen={true} showAnnotations={this.state.showRunAnnotations} showStageAvg={this.state.visibility.stageAvg}
                                onClick1={this.runAnalytic.bind(this)} onClick2={() => { this.child.zoomOut(); }}
                                zoomOutEnabled={this.state.showZoomButton} hasAnalytics={hasAnalytics} isAirHandler={this.props.isAirHandler} />
                            <br /><br />
                        </div>
                    </div>)
                : <div className="centered"><br />No Data found!</div>);

        let extendedLeftButton = this.props.isLongAlert ?
            <div className={"extended-icon extended-left col-sm-1 col-md-1"}>
                <IconButton onClick={this.onExtendedLeftClick}>
                    <i className={"fa-solid fa-arrow-left extended-color"}></i>
                </IconButton>
            </div>
            : null;
        let extendedRightButton = this.props.isLongAlert ?
            <div className={"extended-icon extended-right col-sm-1 col-md-1"}>
                <IconButton onClick={this.onExtendedRightClick} disabled={this.props.extendRightDisabled}>
                    <i className={this.props.extendRightDisabled ? "fa-solid fa-arrow-right" : "fa-solid fa-arrow-right extended-color"}></i>
                </IconButton>
            </div>
            : null;
            
        let extendedPanel = acGraph ?
            <div>{this.renderACGraph(false, true)}</div>
            : (this.props.currentGraph.extended !== null && this.props.currentGraph.extended !== undefined && this.props.currentGraph.extended.series !== null &&
            this.props.currentGraph.extended.series !== undefined && this.props.currentGraph.extended.series[0] !== null && this.props.currentGraph.extended.series[0] !== undefined &&
            this.props.currentGraph.extended.series[0].data[0] !== undefined && this.props.extendedDataList !== null && this.props.extendedDataList !== undefined && this.props.extendedDataList.length ?

                (this.props.isThermostat ?
                    <div className="margin-top-10 row">
                        {extendedLeftButton}
                        <div className="col-sm-12 col-md-8">
                            <GraphComponent graphType={GraphTypes.ASSET} chartData={this.getHighchartsData(this.props.extendedDataList)} key={`${this.props.currentGraph.extended.timeString}-${this.props.extendedDataList.length}-${this.state.visibility}-timeline`} assetType={Equipment.THERMOSTAT} dataMax={this.props.currentGraph.extended.yDataMax} yAxisLabel={this.props.currentGraph.extended.yDataLabel}
                                            visibility={this.state.visibility} rlaValue={this.props.currentGraph.rla} lraValue={this.props.currentGraph.run.lra} flaValue={null} graphAnnotations={this.getHighchartsAnnotations(this.props.currentGraph.extended.graphAnnotations)} 
                                            hasAnalytics={hasAnalytics} title={this.props.currentGraph.extended.timeString} pointStart={this.props.currentGraph.extended.series[0].data[0].timestamp} alertAnnotations={this.getAlertStartEndLines()} isLongAlert={this.props.isLongAlert}
                                            isMultiCT={this.props.currentAsset.isMultiCT} secondAxisType={AxisTypes.NONE} currentAlert={this.props.currentAlert}/>

                            <br /><br />
                        </div>
                        {extendedRightButton}
                        <div className="margin-top-20 col-sm-4 col-md-2">
                            <RunRightPanel isFullScreen={true} showAnnotations={this.state.showRunAnnotations} showStageAvg={this.state.visibility.stageAvg}
                                           onClick1={this.runAnalytic.bind(this)} onClick2={() => { this.child.zoomOut(); }}
                                           onClick3={() => this.onSetpointClick} isThermostat={this.props.isThermostat} isAirHandler={this.props.isAirHandler}
                                           zoomOutEnabled={this.state.showZoomButton} hasAnalytics={hasAnalytics} handleCheckbox={this.handleCheckbox} />
                            <br /><br />
                        </div>
                    </div>
                    : <div className="margin-top-10 row">
                        {extendedLeftButton}
                        <div className="col-sm-10 col-md-8">
                            <GraphComponent graphType={GraphTypes.ASSET} chartData={this.getHighchartsData(this.props.extendedDataList)} key={`${this.props.currentGraph.extended.timeString}-${this.props.extendedDataList.length}-${this.state.visibility}-timeline`} assetType={Equipment.FURNACE} dataMax={this.props.currentGraph.extended.yDataMax}
                                            visibility={this.state.visibility} rlaValue={this.props.currentGraph.rla} lraValue={this.props.currentGraph.run.lra} flaValue={null} graphAnnotations={this.getHighchartsAnnotations(this.props.currentGraph.extended.graphAnnotations)} 
                                            hasAnalytics={hasAnalytics} title={this.props.currentGraph.extended.timeString} alertAnnotations={this.getAlertStartEndLines()} isLongAlert={this.props.isLongAlert}
                                            pointStart={this.props.currentGraph.extended.series[0].data[0].timestamp} secondAxisType={AxisTypes.TEMP} currentAlert={this.props.currentAlert}/>

                            <br /><br />
                        </div>
                        {extendedRightButton}
                        <div className="margin-top-20 col-sm-4 col-md-2">
                            <RunRightPanel isFullScreen={true} showAnnotations={this.state.showRunAnnotations} showStageAvg={this.state.visibility.stageAvg}
                                           onClick1={this.runAnalytic.bind(this)} onClick2={() => { this.child.zoomOut(); }}
                                           zoomOutEnabled={this.state.showZoomButton} hasAnalytics={hasAnalytics} isAirHandler={this.props.isAirHandler} />
                            <br /><br />
                        </div>
                    </div>)
                : <div className="centered"><br />No Data found!</div>);

        let noDataStr = this.props.currentGraph ? "No Data Found!" : "Select an alert to display graph here";

        if (this.props.multiseriesGraph !== undefined && this.props.multiseriesGraph !== null && this.props.multiseriesGraph.series !== null && this.props.multiseriesGraph.series !== undefined) {
            return (
                <div className={"margin-right-25"}>
                    <Tabs>
                        <TabList>
                            <Tab>Trend</Tab>
                            <Tab>Run</Tab>
                            <Tab>Timeline</Tab>
                        </TabList>

                        <TabPanel>
                            <div className="row">
                                <div className="col-sm-12 col-md-10">
                                    <div className={"graph-assets"}>
                                        {multiGraph}
                                        <br /><br />
                                    </div>
                                </div>
                                <div className="col-sm-4 col-md-2">
                                    <RunRightPanel isFullScreen={true} showAnnotations={false}
                                        onClick1={this.runAnalytic.bind(this)} onClick2={() => { this.child.zoomOut(); }} handleCheckbox={this.handleCheckbox}
                                        zoomOutEnabled={this.state.showZoomButton} hasAnalytics={hasAnalytics} />
                                    <br /><br />
                                </div>
                            </div>

                        </TabPanel>

                        <TabPanel>
                            {runPanel}
                        </TabPanel>
                        <TabPanel>
                            {dailyPanel}
                        </TabPanel>
                    </Tabs>
                </div>
            );
        }
        else if (this.props.isThermostat && this.props.currentGraph !== undefined && this.props.currentGraph !== null && Object.keys(this.props.currentGraph).length && !this.props.graphLoading && (this.props.currentGraph.daily !== null)) {
            return (
                <div className={"margin-right-25"}>
                    <Tabs>
                        <TabList>
                            <Tab>Timeline</Tab>
                        </TabList>

                        <TabPanel>
                            {dailyPanel}
                        </TabPanel>
                    </Tabs>
                </div>
            );
        }
        else if (this.props.currentGraph !== undefined && this.props.currentGraph !== null && Object.keys(this.props.currentGraph).length && !this.props.graphLoading && this.props.currentGraph !== undefined && this.props.currentGraph !== null && ((acGraph ? currentSource !== null : this.props.currentGraph.run !== null) || this.props.currentGraph.daily !== null || this.props.currentGraph.extended !== null)) {
            return (
                <div className={"margin-right-25"}>
                    <Tabs>
                        <TabList>
                            <Tab>Run</Tab>
                            <Tab>Timeline</Tab>
                            <Tab>Extended</Tab>
                        </TabList>

                        <TabPanel>
                            {runPanel}
                        </TabPanel>

                        <TabPanel>
                            {dailyPanel}
                        </TabPanel>
                        
                        <TabPanel>
                            {extendedPanel}
                        </TabPanel>
                    </Tabs>
                </div>
            );
        }

        else {
            return (
                <div className={"graph-triage"}>
                    <Tabs>
                        <TabList >
                            <Tab>Run</Tab>
                            <Tab>Timeline</Tab>
                            <Tab>Extended</Tab>
                            <Tab>Trend</Tab>
                        </TabList>

                        <TabPanel>
                            <br />
                            <div className="centered">
                                {noDataStr}
                            </div>
                            <br /><br />
                        </TabPanel>
                        <TabPanel>
                            <br />
                            <div className="centered">
                                {noDataStr}
                            </div>
                            <br /><br />
                        </TabPanel>
                        <TabPanel>
                            <br />
                            <div className="centered">
                                {noDataStr}
                            </div>
                            <br /><br />
                        </TabPanel>
                        <TabPanel>
                            <br />
                            <div className="centered">
                                {noDataStr}
                            </div>
                            <br /><br />
                        </TabPanel>
                    </Tabs>
                </div>
            );
        }

        return (
            <Tabs>
                <TabList>
                    <Tab>Run</Tab>
                    <Tab>Timeline</Tab>
                    <Tab>Extended</Tab>
                </TabList>

                <TabPanel>
                    {runPanel}
                </TabPanel>

                <TabPanel>
                    {dailyPanel}
                </TabPanel>

                <TabPanel>
                    {extendedPanel}
                </TabPanel>
            </Tabs>
        );
    }

    render() {
        if (!this.props.show) {
            return null;
        }
        else {
            let loader = this.props.extendedLoading ? <FullLoader /> : null;
            let content = this.props.asModal ? 
                <div className="fullscreen-modal" style={{ display: this.props.show ? "block" : "none" }}>
                    <div className="big-modal-wrapper"
                         style={{
                             transform: this.props.show ? 'translateY(0vh)' : 'translateY(-100vh)',
                             opacity: this.props.show ? '1' : '0'
                         }}>
    
                        <div className="modal-header" >
                            <h3>Alert Graphs</h3>
                            <span className="close-modal-btn" onClick={e => { this.onClick(e); this.onCancelClick(); }}>×</span>
                        </div>
    
                        <div className="modal-body text-center justify-content-center">
    
                            {this.props.graphLoading ?
                                <div className='centered align-items-center min-height-300'><Spinner animation="border" variant="dark" /></div>
                                : this.renderBody()
                            }
                            {loader}
    
                        </div>
    
                        <div className="modal-footer">
                            <button className="secondary-btn btn-small" onClick={e => { this.onClick(e); this.onCancelClick(); }}>Close</button>
                        </div>
                    </div>
                </div>
                : <div>
                    {this.props.graphLoading ?
                        <div className='centered align-items-center min-height-300'><Spinner animation="border" variant="dark" /></div>
                        : this.renderBody()
                    }
                </div>
                
            
            return (content);
        }
    }
}

export default AlertGraphsModal;